import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import api, { formatApiError } from '../api';

const AuthContext = createContext();

// Map backend snake_case payloads to the existing camelCase shape the UI expects.
const mapUser = (u) =>
  u && {
    id: u.id,
    phone: u.phone,
    name: u.name,
    age: u.age ?? null,
    gender: u.gender || null,
    email: u.email || null,
    photoUrl: u.photo_url || null,
    wallet: u.wallet ?? 0,
    fastagBalance: u.fastag_balance ?? 0,
    totalTrips: u.total_trips ?? 0,
    referralCode: u.referral_code || '',
    pendingDiscountPercent: u.pending_discount_percent ?? 0,
  };

const mapDriver = (d) =>
  d && {
    id: d.id,
    driverId: d.driver_id,
    name: d.full_name,
    phone: d.phone,
    email: d.email,
    drivingLicense: d.driving_license || '',
    experience: `${d.experience_years} years`,
    experienceYears: d.experience_years,
    gender: d.gender || null,
    photoUrl: d.photo_url || null,
    hasLicenseDoc: d.has_license_doc ?? false,
    hasAadhaarDoc: d.has_aadhaar_doc ?? false,
    rejectionReason: d.rejection_reason || null,
    status: d.status,
    rating: d.rating ?? 5.0,
    totalTrips: d.total_trips ?? 0,
    available: d.available ?? false,
    earnings: {
      today: d.earnings_today ?? 0,
      week: d.earnings_week ?? 0,
      month: d.earnings_month ?? 0,
    },
    verified: d.status === 'approved',
  };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'user' | 'driver' | null
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // On mount: try to restore session from cookie
  useEffect(() => {
    const restore = async () => {
      const savedType = localStorage.getItem('userType');
      if (!savedType) {
        setLoading(false);
        return;
      }
      try {
        let path;
        if (savedType === 'driver') path = '/api/auth/driver/me';
        else if (savedType === 'admin') path = '/api/admin/stats'; // any admin-only endpoint restores session
        else path = '/api/auth/user/me';
        const { data } = await api.get(path);
        if (savedType === 'driver') setUser(mapDriver(data));
        else if (savedType === 'admin') setUser({ email: 'admin', name: 'Admin' });
        else setUser(mapUser(data));
        setUserType(savedType);
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('userType');
        localStorage.removeItem('access_token');
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, []);

  const sendUserOtp = useCallback(async (phone) => {
    const { data } = await api.post('/api/auth/user/send-otp', { phone });
    return data; // { sent, dev_otp, expires_in_seconds }
  }, []);

  const verifyUserOtp = useCallback(async (phone, otp) => {
    const { data } = await api.post('/api/auth/user/verify-otp', { phone, otp });
    if (data.access_token) localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('userType', 'user');
    const mapped = mapUser(data.user);
    setUser(mapped);
    setUserType('user');
    setIsAuthenticated(true);
    return mapped;
  }, []);

  // Re-fetch /me and refresh the in-memory user (used after profile edits,
  // photo upload, referral apply, and after a booking consumes a discount).
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/api/auth/user/me');
      const mapped = mapUser(data);
      setUser(mapped);
      return mapped;
    } catch {
      return null;
    }
  }, []);

  const loginDriver = useCallback(async (driverId, password) => {
    const { data } = await api.post('/api/auth/driver/login', {
      driver_id: driverId,
      password,
    });
    if (data.access_token) localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('userType', 'driver');
    const mapped = mapDriver(data.driver);
    setUser(mapped);
    setUserType('driver');
    setIsAuthenticated(true);
    return mapped;
  }, []);

  const loginAdmin = useCallback(async (email, password) => {
    const { data } = await api.post('/api/admin/login', { email, password });
    if (data.access_token) localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('userType', 'admin');
    setUser(data.admin);
    setUserType('admin');
    setIsAuthenticated(true);
    return data.admin;
  }, []);

  const registerDriver = useCallback(async (payload) => {
    const { data } = await api.post('/api/auth/driver/register', payload);
    return data; // { driver_id, password, status, message }
  }, []);

  const logout = useCallback(async () => {
    try {
      const path =
        userType === 'driver' ? '/api/auth/driver/logout'
        : userType === 'admin' ? '/api/admin/logout'
        : '/api/auth/user/logout';
      await api.post(path);
    } catch {
      /* ignore */
    }
    setUser(null);
    setUserType(null);
    setIsAuthenticated(false);
    localStorage.removeItem('userType');
    localStorage.removeItem('access_token');
  }, [userType]);

  const updateUser = useCallback((updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  }, []);

  const value = useMemo(
    () => ({
      user,
      userType,
      isAuthenticated,
      loading,
      sendUserOtp,
      verifyUserOtp,
      loginDriver,
      loginAdmin,
      registerDriver,
      logout,
      updateUser,
      refreshUser,
      formatApiError,
    }),
    [user, userType, isAuthenticated, loading, sendUserOtp, verifyUserOtp, loginDriver, loginAdmin, registerDriver, logout, updateUser, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
